import { Copy, Check, Download } from 'lucide-react';
import { useCallback } from 'react';
import Markdown from '@/components/markdown/Markdown';
import useCopyState from './useCopyState';

interface ArtifactMarkdownReportProps {
  title: string;
  data: { content: string };
}

function ArtifactMarkdownReport({ title, data }: ArtifactMarkdownReportProps) {
  const [copied, triggerCopied] = useCopyState();

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(data.content || '');
    triggerCopied();
  }, [data, triggerCopied]);

  const handleDownload = useCallback(() => {
    const blob = new Blob([data.content || ''], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/\s+/g, '-').toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data, title]);

  if (!data?.content) return null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-neutral-800">{title}</h3>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleCopy}
            className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded hover:bg-neutral-100 transition-colors"
            title="Copy as plain text"
            aria-label="Copy report as plain text"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-emerald-500" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </button>
          <button
            type="button"
            onClick={handleDownload}
            className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded hover:bg-neutral-100 transition-colors"
            title="Download as .md"
            aria-label="Download report as Markdown"
          >
            <Download className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg p-5 prose prose-sm max-w-none">
        <Markdown>{data.content}</Markdown>
      </div>
    </div>
  );
}

export default ArtifactMarkdownReport;
